import {sleep} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {PersistenceInfo} from 'procedures/ProcedurePageContext';
import {
  FlowchartConfigWithMetadataAndRootTaskSchema,
  ProcessFlowchartAdapter,
  ProcessFlowchartAdditionalPersistedData,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartAdapter';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';

import {ProcessFlowchartNodeType, StaticTemplateFlowchart} from '../processFlowchartConfigEditorConstants';
import {
  FlowchartConfigWithMetadata,
  setIsDirty as setFlowchartConfigDirty,
} from './flowchartConfigAutosaveManagerCallbacks';
import {FlowchartConfigMetadata} from './reduceFlowchartConfig';

type SaveHandler = (
  flowchartConfig: FlowchartConfigWithMetadata,
  isBlocking?: boolean,
  persistenceInfo?: PersistenceInfo
) => Promise<null | FlowchartConfigMetadata>;

type SetPendingDraftFlowchartUpdate = null | {
  updater: (fc: StaticTemplateFlowchart) => StaticTemplateFlowchart;
};

/**
 * This factory for making a saveHandler is used to ensure that the saveHandler is protected from
 * unmounted components.
 *
 * Callers must ensure that the callbacks are noops if the component is unmounted. They are only provided
 * to allow components to do bookkeeping, which isn't needed once the component is unmounted.
 *
 * The factory returns a saveHandler that can be passed to the AutosaveManager.
 */
export function saveHandlerFactory(
  setFlowchartConfigMetadata: (m: FlowchartConfigMetadata) => void,
  updateAndPersistFlowchartConfig: (
    flowchartConfig: FlowchartConfigWithMetadata,
    context?: PersistenceInfo
  ) => Promise<null | {
    updatedFlowchartConfig: FlowchartConfigWithMetadata;
    additionalPersistedData: ProcessFlowchartAdditionalPersistedData;
  }>,
  setPendingDraftFlowchartUpdate: (newUpdate: SetPendingDraftFlowchartUpdate) => void,
  processFlowchartConfigId: string
): SaveHandler {
  return (flowchartConfig: FlowchartConfigWithMetadata, isBlocking?: boolean) =>
    saveHandler(flowchartConfig, isBlocking, {
      setFlowchartConfigMetadata,
      updateAndPersistFlowchartConfig,
      setPendingDraftFlowchartUpdate,
      processFlowchartConfigId,
    });
}

interface FactoryOptions {
  setFlowchartConfigMetadata: (m: FlowchartConfigMetadata) => void;
  updateAndPersistFlowchartConfig: (
    flowchartConfig: FlowchartConfigWithMetadata,
    context?: PersistenceInfo
  ) => Promise<null | {
    updatedFlowchartConfig: FlowchartConfigWithMetadata;
    additionalPersistedData: ProcessFlowchartAdditionalPersistedData;
  }>;
  setPendingDraftFlowchartUpdate: (newUpdate: SetPendingDraftFlowchartUpdate) => void;
  processFlowchartConfigId: string;
}

async function saveHandler(
  // --- Provided by the caller (i.e., the AutosaveManager)
  flowchartConfig: FlowchartConfigWithMetadata,
  isBlocking: boolean = false,
  // --- Provided by the factory
  factoryOptions: FactoryOptions
): Promise<FlowchartConfigMetadata | null> {
  const {
    setFlowchartConfigMetadata,
    updateAndPersistFlowchartConfig,
    setPendingDraftFlowchartUpdate,
    processFlowchartConfigId,
  } = factoryOptions;
  setFlowchartConfigDirty(processFlowchartConfigId, false);

  assert(flowchartConfig.metadata.rootTaskSchema != null, 'Root task schema is required');
  const typedFlowchartConfig = flowchartConfig as FlowchartConfigWithMetadataAndRootTaskSchema;

  const flowchartAdapter: ProcessFlowchartAdapter<unknown, unknown> = PLUGIN_REGISTRY_INSTANCE.getNodeAdapter(
    ProcessFlowchartNodeType.TASK_SCHEMA_NODE,
    typedFlowchartConfig.metadata.rootTaskSchema.executionType
  ) as ProcessFlowchartAdapter<unknown, unknown>;
  flowchartAdapter.onSaveStart(typedFlowchartConfig);

  try {
    setFlowchartConfigMetadata(typedFlowchartConfig.metadata);
    await sleep(0); // Ensure that the metadata update is flushed
    const result = await updateAndPersistFlowchartConfig(flowchartConfig);

    if (result != null) {
      // The result should have a non-null rootTaskSchema since we asserted it before saving
      assert(
        result.updatedFlowchartConfig.metadata.rootTaskSchema != null,
        'Root task schema should be present after save'
      );
      const persistedFlowchartConfig =
        result.updatedFlowchartConfig as FlowchartConfigWithMetadataAndRootTaskSchema;

      const updatedFlowchartConfig = await flowchartAdapter.onSaveComplete(
        persistedFlowchartConfig,
        result.additionalPersistedData
      );
      if (updatedFlowchartConfig != null) {
        setPendingDraftFlowchartUpdate({
          updater: (latestDraftFlowchart: StaticTemplateFlowchart) => ({
            nodesStaticData: updatedFlowchartConfig.staticFlowchart.nodesStaticData,
            edgesStaticData: updatedFlowchartConfig.staticFlowchart.edgesStaticData,
          }),
        });
      }
    }
    return result?.updatedFlowchartConfig.metadata ?? null;
  } catch (e) {
    setFlowchartConfigDirty(processFlowchartConfigId, true);
    throw e;
  } finally {
    // TODO: Add event logging
  }
}
